import type { IPublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { PrimeReactProvider } from 'primereact/api'

import * as React from 'react'
import { Route, Routes } from 'react-router'
import { logger } from './common/logger'

import { MainLayout } from './components/MainLayout'
import { NoMatchPage } from './pages/NoMatch'
import { paths } from './paths'
import { UserProvider } from './users/UserProvider'

import 'primereact/resources/themes/lara-light-cyan/theme.css'
import 'primeicons/primeicons.css'
import 'primeflex/primeflex.css'

// Menu.
const UserManagementPage = React.lazy(async () => import('./pages/UserManagement'))
const ZoneManagementPage = React.lazy(async () => import('./pages/ZoneManagement'))
// GO TO sidebar section.
const HomePage = React.lazy(async () => import('./pages/Home'))
const ExcursionsPage = React.lazy(async () => import('./pages/Excursions'))
const VisitingHoursPage = React.lazy(async () => import('./pages/VisitingHoursPage'))
// INFORMATION HUB sidebar section.
const DestinationDatabasePage = React.lazy(async () => import('./pages/DestinationDatabase'))
const DestinationDatabaseSearchResultsPage = React.lazy(async () => import('./pages/DestinationDatabaseSearchResults'))
const DestinationDatabaseQuestionsPage = React.lazy(async () => import('./pages/DestinationDatabaseQuestions'))
const FaqQuestionPage = React.lazy(async () => import('./pages/FaqQuestion'))
// DATA LISTS sidebar section.
const TransferListsPage = React.lazy(async () => import('./pages/TransferLists'))
const TransferListPage = React.lazy(async () => import('./pages/TransferList'))
const RoomingListsPage = React.lazy(async () => import('./pages/RoomingLists'))
const RoomingListPage = React.lazy(async () => import('./pages/RoomingList'))
const InhouseListsPage = React.lazy(async () => import('./pages/InhouseLists'))
const InhouseListPage = React.lazy(async () => import('./pages/InhouseList'))
const AncillariesListsPage = React.lazy(async () => import('./pages/AncillariesLists'))
const AncillariesListPage = React.lazy(async () => import('./pages/AncillariesList'))
const SkiOrdersPage = React.lazy(async () => import('./pages/SkiOrders'))

const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      onError: (error: unknown) => {
        logger.logError(error as Error)
      },
    },
  },
})

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <React.Suspense fallback={<>...</>}>{children}</React.Suspense>
}

interface AppProps {
  pca: IPublicClientApplication
}

export default function App({ pca }: AppProps) {
  return (
    <MsalProvider instance={pca}>
      <UserProvider>
        <QueryClientProvider client={queryClient}>
          <PrimeReactProvider>
            <Routes>
              <Route path={paths.home} element={<MainLayout />}>
                {/* GO TO section */}
                <Route index element={<SuspenseWrapper><HomePage /></SuspenseWrapper>} />
                <Route path={paths.excursions} element={<SuspenseWrapper><ExcursionsPage /></SuspenseWrapper>} />
                <Route path={paths.userManagement} element={<SuspenseWrapper><UserManagementPage /></SuspenseWrapper>} />
                <Route path={paths.zoneManagement} element={<SuspenseWrapper><ZoneManagementPage /></SuspenseWrapper>} />
                {/* DATA LISTS section */}
                <Route path={paths.transferLists} element={<SuspenseWrapper><TransferListsPage /></SuspenseWrapper>} />
                <Route path={paths.transferList} element={<SuspenseWrapper><TransferListPage /></SuspenseWrapper>} />
                <Route path={paths.roomingLists} element={<SuspenseWrapper><RoomingListsPage /></SuspenseWrapper>} />
                <Route path={paths.roomingList} element={<SuspenseWrapper><RoomingListPage /></SuspenseWrapper>} />
                <Route path={paths.ancillariesLists} element={<SuspenseWrapper><AncillariesListsPage /></SuspenseWrapper>} />
                <Route path={paths.ancillariesList} element={<SuspenseWrapper><AncillariesListPage /></SuspenseWrapper>} />
                <Route path={paths.skiOrders} element={<SuspenseWrapper><SkiOrdersPage /></SuspenseWrapper>} />
                <Route path={paths.inhouseLists} element={<SuspenseWrapper><InhouseListsPage /></SuspenseWrapper>} />
                <Route path={paths.inhouseList} element={<SuspenseWrapper><InhouseListPage /></SuspenseWrapper>} />
                <Route path={paths.visitingHours} element={<SuspenseWrapper><VisitingHoursPage /></SuspenseWrapper>} />
                {/* INFORMATION HUB section */}
                <Route path={paths.destinationDatabase} element={<SuspenseWrapper><DestinationDatabasePage /></SuspenseWrapper>} />
                <Route path={paths.destinationDatabaseSearchResults} element={<SuspenseWrapper><DestinationDatabaseSearchResultsPage /></SuspenseWrapper>} />
                <Route path={paths.destinationDatabaseQuestions} element={<SuspenseWrapper><DestinationDatabaseQuestionsPage /></SuspenseWrapper>} />
                <Route path={paths.destinationDatabaseQuestion} element={<SuspenseWrapper><FaqQuestionPage /></SuspenseWrapper>} />
                <Route path="*" element={<NoMatchPage />} />
              </Route>
            </Routes>
          </PrimeReactProvider>
        </QueryClientProvider>
      </UserProvider>
    </MsalProvider>
  )
}
